import {AccountDetailsFormValues} from "../types";
import {
  TConnectBankApiResponse,
  TConnectToStripePayload,
  useConnectBank,
  useConnectToStripe,
} from "../../../../../../../react-query-hooks/payout";
import {usePayoutSettingsContext} from "../../context";
import {handleBankName, hasBankBranch} from "../utils";
import {message} from "antd";
import {isNonEu} from "../../../../../../../utils/bank";
import {useInitiateStripeUpdate} from "../../../../../../../features/bank-accounts/hooks/useStripeActions";
import {useInitiateUpdateBankAccount} from "../../../../../../../features/bank-accounts/hooks/useBankAccounts";
import {useFetchAccountDetailsContext} from "../../../../connect-bank-account/context/fetch-account-details.context";

interface HookResult {
  saveBank(values: AccountDetailsFormValues): void;
  handleSaveBank(values: AccountDetailsFormValues): void;
  saveStripeBank(value: TConnectToStripePayload["countryCode"]): void;
  wasBankSaved: boolean;
  isSavingBank: boolean;
  responseData?: TConnectBankApiResponse;
}

export function useSaveBank(): HookResult {
  const {state, dispatch} = usePayoutSettingsContext();
  const {state: accountDetailsState} = useFetchAccountDetailsContext();
  const mutation = useConnectBank();
  const connectStripeAccountMutation = useConnectToStripe();
  const initiateUpdateBankAccountMutation = useInitiateUpdateBankAccount();
  const initiateStripeUpdateMutation = useInitiateStripeUpdate();

  // Check if current connected bank is Stripe
  const isCurrentBankStripe = accountDetailsState.isStripe;

  function saveBank(values: AccountDetailsFormValues) {
    const bankName = handleBankName({
      banks: state.payoutBanks.banks,
      selectedBank: values.bankCode,
    });
    if (bankName) {
      const isBankBranchAllowed = bankName.hasBankBranch;
      mutation.mutate(
        {
          ...values,
          bankName: bankName.name,
          ...(isBankBranchAllowed &&
            values.branchCode && {branchCode: values.branchCode}),
        },
        {
          onSuccess() {
            message.success("Bank connected successfully");
            dispatch({
              type: "reset-state",
            });
          },
        }
      );
    } else {
      message.error("Bank not found");
    }
  }
  function saveStripeBank(value: TConnectToStripePayload["countryCode"]) {
    const payoutCountry = state.payoutCountries.countries.find((c) => c.code === value);

    if (payoutCountry) {
      const payload: TConnectToStripePayload = {
        countryCode: value,
        countryName: payoutCountry.name,
        currencyCode: payoutCountry.currency,
      };
      connectStripeAccountMutation.mutate(payload, {
        onSuccess() {
          dispatch({type: "reset-state"});
        },
      });
    } else {
      message.error("Country not found");
    }
  }

  const handleSaveBank = (values: AccountDetailsFormValues) => {
    if (
      isNonEu.includes(values.countryCode) ||
      hasBankBranch.includes(values.countryCode)
    ) {
      if (state.currentStep.type === "create") {
        saveBank(values);
      } else {
        // Switching to non-EU country during update
        if (isCurrentBankStripe) {
          // Current bank is Stripe, need to CREATE new non-EU bank (replaces Stripe)
          saveBank(values);
        } else {
          // Current bank is already non-EU, use UPDATE flow
          const bankName = handleBankName({
            banks: state.payoutBanks.banks,
            selectedBank: values.bankCode,
          });
          dispatch({
            type: "update-connect-bank-form-values",
            payload: values,
          });
          if (bankName) {
            dispatch({
              type: "update-connect-bank-name-value",
              payload: bankName.name,
            });
          }
          dispatch({type: "update-update-type", payload: "local"});

          // v2 two-step flow: initiate sends bank details AND triggers OTP
          const isBankBranchAllowed = bankName?.hasBankBranch ?? false;
          initiateUpdateBankAccountMutation.mutate(
            {
              ...values,
              bankName: bankName?.name ?? "",
              ...(isBankBranchAllowed &&
                values.branchCode && {branchCode: values.branchCode}),
            },
            {
              onSuccess() {
                dispatch({
                  type: "update-payment-settings-flow",
                  payload: {
                    type: "update",
                    currentView: "otp",
                  },
                });
              },
            }
          );
        }
      }
    } else {
      if (state.currentStep.type === "create") {
        saveStripeBank(values.countryCode);
      } else {
        // Switching to Stripe country during update - always use Stripe update flow
        // This handles both:
        // 1. Current bank is non-EU (Paystack/Flutterwave) switching to Stripe
        // 2. Current bank is already Stripe, updating country
        dispatch({
          type: "update-connect-bank-form-values",
          payload: values,
        });
        dispatch({type: "update-update-type", payload: "stripe"});
        initiateStripeUpdateMutation.mutate(
          {countryCode: values.countryCode},
          {
            onSuccess() {
              dispatch({
                type: "update-payment-settings-flow",
                payload: {
                  type: "update",
                  currentView: "otp",
                },
              });
            },
          }
        );
      }
    }
  };
  return {
    saveBank,
    saveStripeBank,
    handleSaveBank,
    wasBankSaved: mutation.isSuccess || connectStripeAccountMutation.isSuccess,
    isSavingBank:
      mutation.isLoading ||
      connectStripeAccountMutation.isLoading ||
      initiateStripeUpdateMutation.isLoading ||
      initiateUpdateBankAccountMutation.isLoading,
    responseData: mutation.data?.data,
  };
}
